import { useEffect, useRef } from 'react';

import type { Frame } from './types';
import { SliceRenderer } from './webgl/sliceRenderer';

const MAX_BACKING = 2048;

/** Renders a single HU Frame with GPU window/level (shared with the planes). */
export function FrameCanvas({
    frame,
    center,
    width,
    className
}: {
    frame: Frame | null;
    center: number;
    width: number;
    className?: string;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rendererRef = useRef<SliceRenderer | null>(null);
    const win = useRef({ center, width });
    useEffect(() => {
        win.current = { center, width };
    }, [center, width]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        let r: SliceRenderer | null = null;
        try {
            r = new SliceRenderer(canvas);
        } catch (err) {
            // WebGL2 unavailable on this canvas; leave the renderer null (blank pane).
            console.warn('CBCT frame canvas: renderer creation failed (pane stays blank):', err);
            return;
        }
        rendererRef.current = r;
        const fit = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const w = Math.min(Math.round(canvas.clientWidth * dpr), MAX_BACKING);
            const h = Math.min(Math.round(canvas.clientHeight * dpr), MAX_BACKING);
            if (w > 0 && h > 0) {
                r!.resize(w, h);
                r!.draw(win.current.center, win.current.width);
            }
        };
        const ro = new ResizeObserver(fit);
        ro.observe(canvas);
        return () => {
            ro.disconnect();
            r?.dispose();
            rendererRef.current = null;
        };
    }, []);

    useEffect(() => {
        const r = rendererRef.current;
        const canvas = canvasRef.current;
        if (!r || !canvas || !frame) return;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        r.resize(
            Math.min(Math.round(canvas.clientWidth * dpr), MAX_BACKING) || frame.w,
            Math.min(Math.round(canvas.clientHeight * dpr), MAX_BACKING) || frame.h
        );
        r.setFrame(frame.data, frame.w, frame.h);
        r.draw(win.current.center, win.current.width);
    }, [frame]);

    useEffect(() => {
        rendererRef.current?.draw(center, width);
    }, [center, width]);

    return <canvas ref={canvasRef} className={className} />;
}
